import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { Field, getFormValues, change } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import ClaimAdjustmentCodesTable from 'components/settings/ClaimAdjustmentCodesTable';
import EraActionsTable from 'components/settings/EraActionsTable';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import AutosuggestField from 'components/fields/AutosuggestField';
import EraActionCreateEditDialog from 'components/settings/EraActionCreateEditDialog';

import {
  fetchClaimAdjustmentCodes,
  clearClaimAdjustmentCodes,
  requestSearchClaimAdjustmentCodes,
  requestFetchClaimAdjustmentCodes,
} from 'redux/modules/claimAdjustmentCodes';
import {
  fetchEraActions,
  clearEraActions,
} from 'redux/modules/eraActions';
import { accountLoginFail } from 'redux/modules/account';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

const workspaceTabItems = [
  {
    text: 'Note Categories',
    href: '/settings/workspace/note-categories',
  },
  {
    text: 'Document Categories',
    href: '/settings/workspace/document-categories',
  },
  {
    text: 'Workflow Statuses',
    href: '/settings/workspace/workflow-statuses',
  },
  {
    text: 'Claim Statuses',
    href: '/settings/workspace/claim-statuses',
  },
  {
    text: 'Claim Codes',
    href: '/settings/workspace/claim-adjustment-codes',
  },
];

const constTypeOptions = [
  { value: 'claim', label: 'Claim' },
  { value: 'provider', label: 'Provider' },
  { value: 'group', label: 'Group' },
  { value: 'remark', label: 'Remark' },
  { value: 'actions', label: 'Actions' },
];

const styles = () => ({
  filter: {
    zIndex: 21,
    '&:before': {
      bottom: -16,
      top: 0,
    },
  },
  gridItemFilter: {
    maxWidth: '23%',
    flexBasis: '23%',
  },
});

class WorkspaceClaimAdjustmentCodes extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
      createDialogOpen: false,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearClaimAdjustmentCodes();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters.Code !== filters.Code
      || nextProps.filters['Const Type'] !== filters['Const Type']
      || nextProps.filters['Code ID'] !== filters['Code ID']
      || nextProps.filters['Group ID'] !== filters['Group ID'];
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }
    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      filters,
      claimAdjustmentCodes,
      count,
      loading,
      eraActions,
      eraActionsCount,
      variant,
    } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Code'],
        immediateFilterKeys: ['Const Type', 'Code ID', 'Group ID'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (variant === 'codes'
      && claimAdjustmentCodes.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    } else if (variant === 'actions'
      && eraActions.length < eraActionsCount
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters, variant } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: key === 'Code' ? 'contains' : 'eq',
        val: filters[key],
        ...key === 'Code' ? { case_sensitivity: false } : {},
      }))
      : null;
    if (variant === 'actions') {
      actions.fetchEraActions({
        limit,
        offset,
        filters: requestFilters,
        triggerLoading: true,
        push: offset > 0,
      });
    } else {
      actions.fetchClaimAdjustmentCodes({
        limit,
        offset,
        filters: requestFilters,
        triggerLoading: true,
        push: offset > 0,
      });
    }
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { filters, variant } = this.props;
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if (variant === 'actions') {
        if ((this.props.eraActions.length - limit) >= offset) {
          this.setState({
            offset: offset + limit,
          });
        }
      } else {
        if ((this.props.claimAdjustmentCodes.length - limit) >= offset) {
          this.setState({
            offset: offset + limit,
          });
        }
      }
    }
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  render() {
    const {
      claimAdjustmentCodes,
      count,
      loading,
      filters,
      width,
      eraActions,
      eraActionsCount,
      variant,
      actions,
      classes,
    } = this.props;
    const { mobileFilterExpanded, createDialogOpen } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Claim Codes
          </title>
        </Helmet>
        <TabNavigation
          items={workspaceTabItems}
        />
        <Filter
          className={classes.filter}
          form="claimAdjustmentCodesFilter"
          placeholder="Search by Code"
          searchWidth={5}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={claimAdjustmentCodes}
          top={width === 'xs' || width === 'sm' ? 110 : 126}
          showPrimaryFilter={variant === 'codes'}
          {...variant === 'actions' ? {
            primaryButtonText: 'Add New',
            onPrimaryButtonClick: this.onFilterButtonClick,
            onChange: (values, dispatch, props, prevValues) => {
              if (values['Group ID']
                && values['Selected Group Name']
                && values['Group Name'] !== values['Selected Group Name']) {
                dispatch(change('claimAdjustmentCodesFilter', 'Selected Group Name', null));
                dispatch(change('claimAdjustmentCodesFilter', 'Group ID', null));
              }
              if (values['Code ID']
                && values['Selected Code Name']
                && values['Code Name'] !== values['Selected Code Name']) {
                dispatch(change('claimAdjustmentCodesFilter', 'Selected Code Name', null));
                dispatch(change('claimAdjustmentCodesFilter', 'Code ID', null));
              }
            },
          } : {}}
          secondaryFilter={(
            <Fragment>
              {variant === 'actions' && (
                <Fragment>
                  <Grid
                    className={classes.gridItemFilter}
                    item
                    xs={6}
                    md={3}
                  >
                    <Field
                      id="Group Name"
                      name="Group Name"
                      component={AutosuggestField}
                      placeholder="Search by Group"
                      fullWidth
                      renderSuggestionContent={suggestion => suggestion.Code}
                      renderInputContent={suggestion => suggestion.Code}
                      listWidth="auto"
                      onSuggestionSelected={(suggestion) => {
                        actions.change('claimAdjustmentCodesFilter', 'Selected Group Name', suggestion.Code);
                        actions.change('claimAdjustmentCodesFilter', 'Group ID', suggestion.ID);      
                        return suggestion.Code;
                      }}
                      fetchSuggestions={({ value, limit, offset }) => (
                        requestSearchClaimAdjustmentCodes({
                            filters: [
                              ...value.length > 0
                                ? [
                                    {
                                      name: 'Code',
                                      operation: 'startswith',
                                      val: value,
                                      case_sensitivity: false,
                                    },
                                    {
                                      name: 'Const Type',
                                      operation: 'eq',
                                      val: 'group',
                                    },
                                  ] : [
                                    {
                                      name: 'Const Type',
                                      operation: 'eq',
                                      val: 'group',
                                    },
                                  ],
                            ],
                            limit,
                            offset,
                          }).then(res => res.data,
                            (err) => {
                              if (err.data.error === 'Need authorisation') {
                                actions.accountLoginFail();
                              }
                              return err;
                            })
                      )}
                    />
                  </Grid>
                  <Grid
                    className={classes.gridItemFilter}
                    item
                    xs={6}
                    md={3}
                  >
                    <Field
                      id="Code Name"
                      name="Code Name"
                      component={AutosuggestField}
                      placeholder="Search by Code"
                      fullWidth
                      renderSuggestionContent={suggestion => suggestion.Code}
                      renderInputContent={suggestion => suggestion.Code}
                      listWidth="auto"
                      onSuggestionSelected={(suggestion) => {
                        actions.change('claimAdjustmentCodesFilter', 'Selected Code Name', suggestion.Code);
                        actions.change('claimAdjustmentCodesFilter', 'Code ID', suggestion.ID);             
                        return suggestion.Code;
                      }}
                      fetchSuggestions={({ value, limit, offset }) => (
                        requestSearchClaimAdjustmentCodes({
                            filters: [
                              ...value.length > 0
                                ? [
                                    {
                                      name: 'Code',
                                      operation: 'startswith',
                                      val: value,
                                      case_sensitivity: false,
                                    },
                                    {
                                      name: 'Const Type',
                                      operation: 'eq',
                                      val: 'claim',
                                    },
                                  ] : [
                                    {
                                      name: 'Const Type',
                                      operation: 'eq',
                                      val: 'claim',
                                    },
                                  ],
                            ],
                            limit,
                            offset,
                          }).then(res => res.data,
                            (err) => {
                              if (err.data.error === 'Need authorisation') {
                                actions.accountLoginFail();
                              }
                              return err;
                            })
                      )}
                    />
                  </Grid>
                </Fragment>
              )}
              <Grid item>
                <Paper>
                  <FormControl role="group">
                    <Field
                      name="Const Type"
                      component={RadioGroupField}
                    >
                      {constTypeOptions.map(item => (
                        <RadioButtonField
                          key={item.value}
                          value={item.value}
                          label={item.label}
                          color="primary"
                        />
                      ))}
                    </Field>
                  </FormControl>
                </Paper>
              </Grid>
            </Fragment>
          )}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        {variant === 'actions' ? (
          <Fragment>
            <EraActionsTable
              items={eraActions}
              count={eraActionsCount}
              loading={loading}
              mobileFilterExpanded={mobileFilterExpanded}
              onScroll={this.onTableScroll}
              scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
            />
            <EraActionCreateEditDialog
              form="eraActionCreate"
              open={createDialogOpen}
              onClose={this.onCreateDialogClose}
              onSubmitSuccess={this.onCreateDialogClose}
            />
          </Fragment>
        ) : (
          <ClaimAdjustmentCodesTable
            items={claimAdjustmentCodes}
            count={count}
            loading={loading}
            mobileFilterExpanded={mobileFilterExpanded}
            onScroll={this.onTableScroll}
            scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
          />
        )}
      </Layout>
    );
  }
}

WorkspaceClaimAdjustmentCodes.propTypes = {
  claimAdjustmentCodes: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  variant: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    Code: PropTypes.string,
    'Const Type': PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchClaimAdjustmentCodes: PropTypes.func.isRequired,
    clearClaimAdjustmentCodes: PropTypes.func.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    filter: PropTypes.string.isRequired,
  }).isRequired,
};

WorkspaceClaimAdjustmentCodes.defaultProps = {};

const mapStateToProps = (store) => {
  const claimAdjustmentCodesFilterValues = getFormValues('claimAdjustmentCodesFilter')(store);
  const variant = claimAdjustmentCodesFilterValues
    && claimAdjustmentCodesFilterValues['Const Type']
    && claimAdjustmentCodesFilterValues['Const Type'] === 'actions'
      ? 'actions'
      : 'codes';
  return {
    variant,
    claimAdjustmentCodes: store.claimAdjustmentCodes.data,
    eraActions: store.eraActions.data,
    eraActionsCount: store.eraActions.count,
    count: store.claimAdjustmentCodes.count,
    filters: {
      ...variant === 'actions'
        && claimAdjustmentCodesFilterValues
        && claimAdjustmentCodesFilterValues['Code ID']
        ? { 'Code ID': claimAdjustmentCodesFilterValues['Code ID'] }
        : {},
      ...variant === 'actions'
        && claimAdjustmentCodesFilterValues
        && claimAdjustmentCodesFilterValues['Group ID']
        ? { 'Group ID': claimAdjustmentCodesFilterValues['Group ID'] }
        : {},
      ...variant === 'codes'
        && claimAdjustmentCodesFilterValues
        && claimAdjustmentCodesFilterValues.Filter
        ? { Code: claimAdjustmentCodesFilterValues.Filter }
        : {},
      ...variant === 'codes'
        &&claimAdjustmentCodesFilterValues
        && claimAdjustmentCodesFilterValues['Const Type']
        ? { 'Const Type': claimAdjustmentCodesFilterValues['Const Type'] }
        : {},
    },
    loading: store.claimAdjustmentCodes.loading + store.eraActions.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchClaimAdjustmentCodes,
    clearClaimAdjustmentCodes,
    fetchEraActions,
    clearEraActions,
    accountLoginFail,
    change,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withWidth()(WorkspaceClaimAdjustmentCodes)),
);



// WEBPACK FOOTER //
// ./src/pages/settings/WorkspaceClaimAdjustmentCodes.js